import { Play, Pause, RotateCcw, RotateCw, Volume2, Share2 } from "lucide-react";
import { usePodcastPlayer } from "@/context/PodcastPlayerContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@/lib/router-compat";
import { useT } from "@/i18n/useLocale";
import T from "@/i18n/T";

interface FullPodcastPlayerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FullPodcastPlayer = ({ open, onOpenChange }: FullPodcastPlayerProps) => {
  const t = useT();
  const navigate = useNavigate();
  const {
    currentEpisode,
    isPlaying,
    pause,
    resume,
    stopPlayback,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackRate,
    seek,
    setVolume,
    toggleMute,
    setPlaybackRate,
  } = usePodcastPlayer();

  if (!currentEpisode) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSeek = (value: number[]) => {
    seek(value[0]);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
  };

  const cyclePlaybackRate = () => {
    const rates = [1, 1.25, 1.5, 1.75, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
  };

  const handleViewEpisode = () => {
    navigate(`/podcasts/episode/${currentEpisode.id}`);
    onOpenChange(false);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: currentEpisode.title,
          text: `Listen to ${currentEpisode.title} on ${currentEpisode.podcast?.name || 'Podcast'}`,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="h-[90vh] p-0 border-t-2 border-border bg-background"
      >
        <div className="h-full flex flex-col bg-background">
          {/* Drag handle */}
          <div className="flex justify-center pt-4 pb-2">
            <div className="w-12 h-1 rounded-full bg-border" />
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {/* Episode artwork */}
            <div className="w-full max-w-sm mx-auto mb-8 mt-4">
              <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 shadow-2xl">
                {currentEpisode.image_url ? (
                  <img
                    src={currentEpisode.image_url}
                    alt={currentEpisode.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-6xl">🎙️</span>
                  </div>
                )}
              </div>
            </div>

            {/* Episode info */}
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-foreground mb-2 line-clamp-2">
                {currentEpisode.title}
              </h2>
              <p className="text-sm text-muted-foreground">
                {currentEpisode.podcast?.name || "Podcast"}
              </p>
            </div>

            {/* Progress slider */}
            <div className="mb-6">
              <Slider
                value={[currentTime]}
                max={duration}
                step={1}
                onValueChange={handleSeek}
                className="mb-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Main controls */}
            <div className="flex items-center justify-center gap-6 mb-8">
              <button
                onClick={() => seek(Math.max(0, currentTime - 15))}
                className="w-12 h-12 rounded-full hover:bg-muted transition-colors flex items-center justify-center group"
                aria-label={t("auto.skip-back-15-seconds-3196")}
              >
                <RotateCcw className="w-6 h-6 text-muted-foreground group-hover:text-foreground" />
                <span className="absolute text-[10px] font-bold mt-1">15</span>
              </button>

              <button
                onClick={() => isPlaying ? pause() : resume()}
                className="w-16 h-16 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg hover:scale-105 flex items-center justify-center"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? (
                  <Pause className="w-8 h-8 fill-primary-foreground" />
                ) : (
                  <Play className="w-8 h-8 ml-1 fill-primary-foreground" />
                )}
              </button>

              <button
                onClick={() => seek(Math.min(duration, currentTime + 15))}
                className="w-12 h-12 rounded-full hover:bg-muted transition-colors flex items-center justify-center group"
                aria-label={t("auto.skip-forward-15-seconds-087f")}
              >
                <RotateCw className="w-6 h-6 text-muted-foreground group-hover:text-foreground" />
                <span className="absolute text-[10px] font-bold mt-1">15</span>
              </button>
            </div>

            {/* Secondary controls */}
            <div className="flex items-center justify-between mb-8 px-4">
              {/* Volume */}
              <div className="flex items-center gap-2 flex-1 max-w-[120px]">
                <button
                  onClick={toggleMute}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  <Volume2 className="w-5 h-5" />
                </button>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  max={1}
                  step={0.01}
                  onValueChange={handleVolumeChange}
                  className="flex-1"
                />
              </div>

              {/* Playback speed */}
              <button
                onClick={cyclePlaybackRate}
                className="px-4 py-2 rounded-full bg-muted hover:bg-muted/80 transition-colors text-sm font-medium"
              >
                {playbackRate}x
              </button>

              {/* Share */}
              <button
                onClick={handleShare}
                className="w-10 h-10 rounded-full hover:bg-muted transition-colors flex items-center justify-center"
                aria-label={t("auto.share-5a95")}
              >
                <Share2 className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            {/* Episode description */}
            <div className="bg-muted/50 rounded-xl p-4 mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-2">
                <T k="auto.episode-description-d2d4" />
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-4">
                {currentEpisode.description}
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleViewEpisode}
                variant="outline"
                className="flex-1"
              >
                <T k="auto.view-episode-5504" />
              </Button>
              <Button
                onClick={() => {
                  stopPlayback();
                  onOpenChange(false);
                }}
                variant="outline"
                className="flex-1"
              >
                <T k="auto.stop-close-b6ae" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FullPodcastPlayer;
